// ============================================================
// HowItWorks.js
// Animated step-by-step journey page
// Place in: frontend/src/pages/HowItWorks.js
// CSS file:  frontend/src/styles/howitworks.css
// ============================================================

import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Login from './Login';
import '../styles/howitworks.css';

const ProfessionalIcon = ({
    name,
    size = 22,
    strokeWidth = 1.8,
    className = ''
}) => {
    const commonProps = {
        width: size,
        height: size,
        viewBox: "0 0 24 24",
        fill: "none",
        stroke: "currentColor",
        strokeWidth: strokeWidth,
        strokeLinecap: "round",
        strokeLinejoin: "round",
        className: className,
        "aria-hidden": "true"
    };

    const icons = {
        phone: (
            <svg {...commonProps}>
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
        ),
        flow: (
            <svg {...commonProps}>
                <rect x="3" y="4" width="6" height="5" rx="1" />
                <rect x="15" y="15" width="6" height="5" rx="1" />
                <rect x="15" y="4" width="6" height="5" rx="1" />
                <path d="M9 6.5h4a2 2 0 0 1 2 2v6.5" />
                <path d="M15 17.5h-4a2 2 0 0 1-2-2V9" />
            </svg>
        ),
     ai: (
            <span 
                className={className}
                style={{
                    display: 'inline-block',
                    width: size,
                    height: size,
                    backgroundColor: 'currentColor',
                    WebkitMask: 'url(/icons/ai.svg) no-repeat center',
                    WebkitMaskSize: 'contain',
                    mask: 'url(/icons/ai.svg) no-repeat center',
                    maskSize: 'contain'
                }}
            />
        ),
        rocket: (
            <svg {...commonProps}>
                <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
                <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
                <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 3 0 3 0" />
                <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-3 0-3" />
            </svg>
        ),
        chart: (
            <svg {...commonProps}>
                <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
            </svg>
        ),
        settings: (
            <svg {...commonProps}>
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-1.8 1.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V20h-2.6v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.9.3l-.1.1-1.8-1.8.1-.1A1.7 1.7 0 0 0 8 15a1.7 1.7 0 0 0-1.5-1H6v-2.6h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.9l-.1-.1L9 6.6l.1.1a1.7 1.7 0 0 0 1.9.3 1.7 1.7 0 0 0 1-1.5V5h2.6v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1 1.8 1.8-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.5 1h.1V14h-.1a1.7 1.7 0 0 0-1.1 1Z" />
            </svg>
        ),
        sparkle: (
            <svg {...commonProps}>
                <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z" />
            </svg>
        ),
        gem: (
            <svg {...commonProps}>
                <path d="M6 3h12l4 6-10 13L2 9Z" />
                <path d="M11 3 8 9l4 13 4-13-3-6" />
                <path d="M2 9h20" />
            </svg>
        ),
        phoneCall: (
            <svg {...commonProps}>
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
        ),
        lock: (
            <svg {...commonProps}>
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
        ),
        bolt: (
            <svg {...commonProps}>
                <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />
            </svg>
        ),
        success: (
            <svg {...commonProps}>
                <circle cx="12" cy="12" r="9" />
                <path d="m8 12 2.5 2.5L16 9" />
            </svg>
        ),
        arrowUp: (
            <svg {...commonProps}>
                <path d="m5 12 7-7 7 7M12 19V5" />
            </svg>
        ),
        arrowDown: (
            <svg {...commonProps}>
                <path d="m19 12-7 7-7-7M12 5v14" />
            </svg>
        ),
        message: (
            <svg {...commonProps}>
                <path d="M20 11.5a8 8 0 0 1-8 7.5 8.8 8.8 0 0 1-3.5-.7L4 20l1.7-4.1A7.7 7.7 0 0 1 4 11.5a8 8 0 1 1 16 0Z" />
            </svg>
        ),
        link: (
            <svg {...commonProps}>
                <path d="M10 13a5 5 0 0 0 7.07.07l2-2a5 5 0 0 0-7.07-7.07l-1.15 1.15" />
                <path d="M14 11a5 5 0 0 0-7.07-.07l-2 2A5 5 0 0 0 12 20l1.15-1.15" />
            </svg>
        ),
        mail: (
            <svg {...commonProps}>
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="m3 7 9 6 9-6" />
            </svg>
        ),
        users: (
            <svg {...commonProps}>
                <circle cx="9" cy="8" r="3" />
                <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
                <circle cx="17" cy="9" r="2" />
                <path d="M17 14c2.2 0 4 1.8 4 4" />
            </svg>
        ),
        route: (
            <svg {...commonProps}>
                <circle cx="6" cy="5" r="2" />
                <circle cx="18" cy="19" r="2" />
                <path d="M8 5h4a3 3 0 0 1 3 3v1a3 3 0 0 0 3 3" />
                <path d="M18 12v5" />
            </svg>
        ),
        clock: (
            <svg {...commonProps}>
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
            </svg>
        ),
        globe: (
            <svg {...commonProps}>
                <circle cx="12" cy="12" r="9" />
                <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
            </svg>
        )
    };

    return icons[name] || null;
};

function HowItWorks() {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [loginModalOpen, setLoginModalOpen] = useState(false);
    const isAuthenticated = !!localStorage.getItem('token');
    const journeyRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const targets = document.querySelectorAll(
            '.hiw-animate, .hiw-animate-left, .hiw-animate-right'
        );
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('hiw-visible');
                    }
                });
            },
            { threshold: 0.15 }
        );
        targets.forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const steps = [
        {
            num: '01',
            tag: 'Step 1 — Account Setup',
            icon: 'phone',
            title: 'Connect Your WhatsApp Business Account',
            desc: 'Start by linking your WhatsApp Business number to ChatTech24. Our guided onboarding gets you verified and connected through the official Meta Business API in minutes.',
            bullets: [
                'Create or link your Meta Business account',
                'Verify your WhatsApp Business phone number',
                'Get your API credentials auto-configured',
                'Set your business display name & profile'
            ],
            visual: {
                icon: 'phone',
                title: 'WhatsApp API Connected',
                sub: 'Meta Business Verified',
                type: 'api'
            }
        },
        {
            num: '02',
            tag: 'Step 2 — Flow Builder',
            icon: 'flow',
            title: 'Build Your Conversation Flow Visually',
            desc: 'Use our intuitive drag-and-drop React Flow Builder with sendMessage, interactive buttons, conditions, wait-for-reply paths, and multi-language auto-detection.',
            bullets: [
                'Drag & drop message and interactive button nodes',
                'Configure branching conditions and multi-choice replies',
                'Add media (image/document) and multi-language support',
                'Live WhatsApp-style simulator preview before publishing'
            ],
            visual: {
                icon: 'flow',
                title: 'Visual Flow Builder',
                sub: 'Drag-and-drop editor',
                type: 'flow'
            }
        },
        {
            num: '03',
            tag: 'Step 3 — AI Assistant',
            icon: 'ai',
            title: 'Add an AI Assistant to Handle Complex Queries',
            desc: 'Create dedicated AI assistants with custom models, instructions, and temperature, then drop them right into your flow as a smart node with dual output branches.',
            bullets: [
                'Create multiple reusable AI assistants for distinct use cases',
                'Full control over custom prompts, instructions, and tone',
                'Seamless Done path to continue flow or Handoff path for human team',
                'No coding needed — just drop the AI node into any conversation flow'
            ],
            visual: {
                icon: 'ai',
                title: 'AI Assistant Node',
                sub: 'Dual Output (Done / Handoff)',
                type: 'ai'
            }
        },
        {
            num: '04',
            tag: 'Step 4 — Go Live',
            icon: 'rocket',
            title: 'Test, Then Go Live',
            desc: 'Preview your exact WhatsApp conversation flow using the built-in simulator, ensure everything works seamlessly, and publish with one click to start serving customers.',
            bullets: [
                'Preview the exact WhatsApp conversation before publishing',
                'One-click publish to make your automated flow live instantly',
                'Automatic replies and 24/7 engagement start immediately',
                'Broadcast updates or scheduled campaigns when needed'
            ],
            visual: {
                icon: 'rocket',
                title: 'Bot is Live!',
                sub: 'Serving customers 24/7',
                type: 'live'
            }
        },
        {
            num: '05',
            tag: 'Step 5 — Analytics',
            icon: 'chart',
            title: 'Track Performance & Optimize',
            desc: 'The ChatTech24 analytics dashboard gives you deep insights into message volume, flow performance, and contact growth to continuously improve your engagement.',
            bullets: [
                'View message volume, open rates, and user response metrics',
                'Identify drop-off points and optimize conversation flows',
                'Monitor AI resolution rates and smart escalation frequency',
                'Export detailed reports and share insights with your team'
            ],
            visual: {
                icon: 'chart',
                title: 'Analytics Dashboard',
                sub: 'Real-time insights',
                type: 'analytics'
            }
        }
    ];

    const renderVisual = (visual) => {
        switch (visual.type) {
            case 'api':
                return (
                    <>
                        <div className="hiw-visual-icon-row">
                            <span className="hiw-visual-main-icon" style={{ background: 'rgba(37, 211, 102, 0.12)', color: '#128C7E', padding: '10px', borderRadius: '12px', display: 'flex' }}>
                                <ProfessionalIcon name={visual.icon} size={28} />
                            </span>
                            <div>
                                <div className="hiw-visual-title">{visual.title}</div>
                                <div className="hiw-visual-sub">{visual.sub}</div>
                            </div>
                        </div>
                        <div className="hiw-mock-bar"><div className="hiw-mock-bar-fill" style={{ width: '100%' }} /></div>
                        <div className="hiw-mock-bar"><div className="hiw-mock-bar-fill" style={{ width: '75%', animationDelay: '0.4s' }} /></div>
                        <div className="hiw-mock-tags">
                            <span className="hiw-mock-tag hiw-mock-tag-green"><span style={{ display: 'inline-flex', verticalAlign: 'middle', marginRight: '4px' }}><ProfessionalIcon name="success" size={13} /></span>API Verified</span>
                            <span className="hiw-mock-tag hiw-mock-tag-blue">Meta Business</span>
                            <span className="hiw-mock-tag hiw-mock-tag-green"><span style={{ display: 'inline-flex', verticalAlign: 'middle', marginRight: '4px' }}><ProfessionalIcon name="lock" size={13} /></span>Secure</span>
                        </div>
                        <div className="hiw-stat-row">
                            <div className="hiw-stat-box">
                                <div className="hiw-stat-num">2 min</div>
                                <div className="hiw-stat-label">Setup time</div>
                            </div>
                            <div className="hiw-stat-box">
                                <div className="hiw-stat-num">99.9%</div>
                                <div className="hiw-stat-label">Uptime SLA</div>
                            </div>
                        </div>
                    </>
                );

            case 'flow':
                return (
                    <>
                        <div className="hiw-visual-icon-row">
                            <span className="hiw-visual-main-icon" style={{ background: 'rgba(18, 140, 126, 0.1)', color: '#128C7E', padding: '10px', borderRadius: '12px', display: 'flex' }}>
                                <ProfessionalIcon name={visual.icon} size={28} />
                            </span>
                            <div>
                                <div className="hiw-visual-title">{visual.title}</div>
                                <div className="hiw-visual-sub">{visual.sub}</div>
                            </div>
                        </div>
                        <div className="hiw-flow-mock">
                            <div className="hiw-flow-node hiw-flow-node-active">Greeting</div>
                            <span className="hiw-flow-arrow">→</span>
                            <div className="hiw-flow-node">Services</div>
                            <span className="hiw-flow-arrow">→</span>
                            <div className="hiw-flow-node hiw-flow-node-active">Demo</div>
                        </div>
                        <div className="hiw-flow-mock" style={{ marginTop: 6 }}>
                            <div className="hiw-flow-node">Products</div>
                            <span className="hiw-flow-arrow">→</span>
                            <div className="hiw-flow-node hiw-flow-node-active">AI Reply</div>
                            <span className="hiw-flow-arrow">→</span>
                            <div className="hiw-flow-node">Agent</div>
                        </div>
                        <div className="hiw-mock-tags" style={{ marginTop: 10 }}>
                            <span className="hiw-mock-tag hiw-mock-tag-purple">50+ Templates</span>
                            <span className="hiw-mock-tag hiw-mock-tag-green">No Code</span>
                            <span className="hiw-mock-tag hiw-mock-tag-blue">Live Preview</span>
                        </div>
                    </>
                );

            case 'ai':
                return (
                    <>
                        <div className="hiw-visual-icon-row">
                            <span className="hiw-visual-main-icon" style={{ background: 'rgba(37, 211, 102, 0.15)', color: '#065f46', padding: '10px', borderRadius: '12px', display: 'flex' }}>
                                <ProfessionalIcon name={visual.icon} size={28} />
                            </span>
                            <div>
                                <div className="hiw-visual-title">{visual.title}</div>
                                <div className="hiw-visual-sub">{visual.sub}</div>
                            </div>
                        </div>
                        <div className="hiw-flow-mock" style={{ marginBottom: 10 }}>
                            <div className="hiw-flow-node hiw-flow-node-active">AI Assistant Node</div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', fontSize: '11px', fontWeight: 600, marginBottom: '8px' }}>
                            <span style={{ background: '#dcfce6', color: '#166534', padding: '4px 8px', borderRadius: '6px' }}>✓ Done (Continue Flow)</span>
                            <span style={{ background: '#fef3c7', color: '#92400e', padding: '4px 8px', borderRadius: '6px' }}>→ Handoff (Agent)</span>
                        </div>
                        <div className="hiw-wa-mini" style={{ marginTop: 4 }}>
                            <div className="hiw-wa-mini-msg hiw-wa-mini-bot">
                                Hello! I am your AI assistant. How can I help with your query today?
                            </div>
                        </div>
                        <div className="hiw-mock-tags">
                            <span className="hiw-mock-tag hiw-mock-tag-orange">Custom Prompt</span>
                            <span className="hiw-mock-tag hiw-mock-tag-green">Smart Routing</span>
                        </div>
                    </>
                );

            case 'live':
                return (
                    <>
                        <div className="hiw-visual-icon-row">
                            <span className="hiw-visual-main-icon" style={{ background: 'rgba(37, 211, 102, 0.12)', color: '#128C7E', padding: '10px', borderRadius: '12px', display: 'flex' }}>
                                <ProfessionalIcon name={visual.icon} size={28} />
                            </span>
                            <div>
                                <div className="hiw-visual-title">
                                    <span className="hiw-live-dot" />
                                    {visual.title}
                                </div>
                                <div className="hiw-visual-sub">{visual.sub}</div>
                            </div>
                        </div>
                        <div className="hiw-stat-row">
                            <div className="hiw-stat-box">
                                <div className="hiw-stat-num">1,284</div>
                                <div className="hiw-stat-label">Msgs today</div>
                            </div>
                            <div className="hiw-stat-box">
                                <div className="hiw-stat-num">98%</div>
                                <div className="hiw-stat-label">Auto-resolved</div>
                            </div>
                        </div>
                        <div className="hiw-mock-bar" style={{ marginTop: 12 }}>
                            <div className="hiw-mock-bar-fill" style={{ width: '98%' }} />
                        </div>
                        <div className="hiw-mock-tags">
                            <span className="hiw-mock-tag hiw-mock-tag-green"><span style={{ display: 'inline-flex', verticalAlign: 'middle', marginRight: '4px' }}><ProfessionalIcon name="success" size={13} /></span>24/7 Active</span>
                            <span className="hiw-mock-tag hiw-mock-tag-blue">Instant Reply</span>
                        </div>
                    </>
                );

            case 'analytics':
                return (
                    <>
                        <div className="hiw-visual-icon-row">
                            <span className="hiw-visual-main-icon" style={{ background: 'rgba(18, 140, 126, 0.1)', color: '#128C7E', padding: '10px', borderRadius: '12px', display: 'flex' }}>
                                <ProfessionalIcon name={visual.icon} size={28} />
                            </span>
                            <div>
                                <div className="hiw-visual-title">{visual.title}</div>
                                <div className="hiw-visual-sub">{visual.sub}</div>
                            </div>
                        </div>
                        <div className="hiw-stat-row">
                            <div className="hiw-stat-box">
                                <div className="hiw-stat-num"><span style={{ display: 'inline-flex', verticalAlign: 'middle' }}><ProfessionalIcon name="arrowUp" size={16} /></span> 40%</div>
                                <div className="hiw-stat-label">Satisfaction</div>
                            </div>
                            <div className="hiw-stat-box">
                                <div className="hiw-stat-num"><span style={{ display: 'inline-flex', verticalAlign: 'middle' }}><ProfessionalIcon name="arrowDown" size={16} /></span> 60%</div>
                                <div className="hiw-stat-label">Support cost</div>
                            </div>
                        </div>
                        <div className="hiw-mock-bar" style={{ marginTop: 12 }}>
                            <div className="hiw-mock-bar-fill" style={{ width: '88%' }} />
                        </div>
                        <div className="hiw-mock-bar">
                            <div className="hiw-mock-bar-fill" style={{ width: '65%', animationDelay: '0.5s' }} />
                        </div>
                        <div className="hiw-mock-tags">
                            <span className="hiw-mock-tag hiw-mock-tag-purple">Deep Reports</span>
                            <span className="hiw-mock-tag hiw-mock-tag-green">CSV Export</span>
                        </div>
                    </>
                );

            default:
                return null;
        }
    };

    return (
        <div className="hiw-page">
            <nav className={`ct-nav ${scrolled ? 'ct-nav-scrolled' : ''}`}>
                <div className="ct-nav-brand" onClick={() => navigate('/')}>
                    <img src="/logo.png" alt="ChatTech24" style={{ height: '44px', width: 'auto', objectFit: 'contain' }} />
                </div>

                <div className="ct-nav-links">
                    <Link to="/how-it-works" className="ct-nav-link" style={{ color: '#128C7E', fontWeight: 700 }}>How It Works</Link>
                    <Link to="/features" className="ct-nav-link">Features</Link>
                    <Link to="/services" className="ct-nav-link">Services</Link>
                    <Link to="/pricing" className="ct-nav-link">Pricing</Link>
                    <Link to="/contact" className="ct-nav-link">Contact</Link>
                </div>

                <div className="ct-nav-actions">
                    {isAuthenticated ? (
                        <button onClick={() => navigate('/dashboard')} className="ct-btn-primary">Dashboard →</button>
                    ) : (
                        <>
                            <button onClick={() => setLoginModalOpen(true)} className="ct-btn-ghost">Login</button>
                            <button onClick={() => navigate('/pricing')} className="ct-btn-primary">Get Started</button>
                        </>
                    )}
                </div>

                <button className="ct-nav-hamburger" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                    <span /><span /><span />
                </button>
            </nav>

            {mobileMenuOpen && (
                <div className="ct-nav-mobile">
                    <div className="ct-mobile-menu-header">
                        <div 
                            className="ct-mobile-menu-brand" 
                            onClick={() => { navigate('/'); setMobileMenuOpen(false); }} 
                            style={{ gap: '2px', cursor: 'pointer' }}
                        >
                            <img src="/logo 1.png" alt="ChatTech24" style={{ height: '36px', width: 'auto', objectFit: 'contain' }} />
                            <span style={{ color: '#D3D3D3', fontFamily: 'Sora, sans-serif', fontSize: '12px', fontWeight: '700' }}>ChatTech24</span>
                        </div>
                        <button className="ct-mobile-menu-close" onClick={() => setMobileMenuOpen(false)}>✕</button>
                    </div>
                    <div className="ct-mobile-menu-links">
                        <Link to="/how-it-works" className="ct-mobile-link" onClick={() => setMobileMenuOpen(false)}>
                            <span className="ct-mobile-link-icon"><ProfessionalIcon name="settings" size={18} /></span>
                            <span>How It Works</span>
                            <span className="ct-mobile-link-arrow">›</span>
                        </Link>
                        <Link to="/features" className="ct-mobile-link" onClick={() => setMobileMenuOpen(false)}>
                            <span className="ct-mobile-link-icon"><ProfessionalIcon name="sparkle" size={18} /></span>
                            <span>Features</span>
                            <span className="ct-mobile-link-arrow">›</span>
                        </Link>
                        <Link to="/services" className="ct-mobile-link" onClick={() => setMobileMenuOpen(false)}>
                            <span className="ct-mobile-link-icon"><ProfessionalIcon name="rocket" size={18} /></span>
                            <span>Services</span>
                            <span className="ct-mobile-link-arrow">›</span>
                        </Link>
                        <Link to="/pricing" className="ct-mobile-link" onClick={() => setMobileMenuOpen(false)}>
                            <span className="ct-mobile-link-icon"><ProfessionalIcon name="gem" size={18} /></span>
                            <span>Pricing</span>
                            <span className="ct-mobile-link-arrow">›</span>
                        </Link>
                        <Link to="/contact" className="ct-mobile-link" onClick={() => setMobileMenuOpen(false)}>
                            <span className="ct-mobile-link-icon"><ProfessionalIcon name="phoneCall" size={18} /></span>
                            <span>Contact</span>
                            <span className="ct-mobile-link-arrow">›</span>
                        </Link>
                    </div>
                    <div className="ct-mobile-menu-actions">
                        <button className="ct-mobile-btn-login" onClick={() => { setLoginModalOpen(true); setMobileMenuOpen(false); }}>
                            👤 Login
                        </button>
                        <button className="ct-mobile-btn-cta" onClick={() => { navigate('/pricing'); setMobileMenuOpen(false); }}>
                            🚀 Get Started
                        </button>
                    </div>
                    <div className="ct-mobile-menu-footer">
                        <span><ProfessionalIcon name="lock" size={14} /> Secure & Reliable</span>
                        <span><ProfessionalIcon name="bolt" size={14} /> 99.9% Uptime</span>
                    </div>
                </div>
            )}

            <div className="hiw-header">
                <div className="hiw-header-badge">
                    <ProfessionalIcon name="settings" size={14} /> Simple Process
                </div>
                <h1 className="hiw-header-title">
                    How <span>ChatTech24</span> Works
                </h1>
                <p className="hiw-header-subtitle">
                    From WhatsApp connection to AI-powered automation — get your chatbot live in under 30 minutes. Here's your complete step-by-step journey.
                </p>
            </div>

            <div className="hiw-journey" ref={journeyRef}>
                <div className="hiw-spine" />

                {steps.map((step, i) => (
                    <div key={i}>
                        <div className="hiw-step">
                            <div className={`hiw-step-content ${i % 2 === 0 ? 'hiw-animate-left' : 'hiw-animate-right'}`}>
                                <div className="hiw-step-tag">
                                    <span>{step.num}</span>
                                    <span>{step.tag.split('—')[1]?.trim() || step.tag}</span>
                                </div>
                                <h2 className="hiw-step-title">{step.title}</h2>
                                <p className="hiw-step-desc">{step.desc}</p>
                                <ul className="hiw-step-bullets">
                                    {step.bullets.map((b, bi) => (
                                        <li key={bi}>
                                            <span className="hiw-bullet-dot" />
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="hiw-step-node hiw-animate">
                                <div className="hiw-node-circle" style={{ color: '#ffffff' }}>
                                    <ProfessionalIcon name={step.icon} size={26} />
                                    <div className="hiw-node-num">{i + 1}</div>
                                </div>
                            </div>

                            <div className={`hiw-step-visual ${i % 2 === 0 ? 'hiw-animate-right' : 'hiw-animate-left'}`}>
                                <div className="hiw-visual-card">
                                    {renderVisual(step.visual)}
                                </div>
                            </div>
                        </div>

                        {i < steps.length - 1 && (
                            <div className="hiw-connector">
                                <div className="hiw-connector-line" />
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <div className="hiw-integrations">
                <div className="hiw-integrations-title">Works seamlessly with</div>
                <div className="hiw-marquee-wrapper">
                    <div className="hiw-marquee-track">
                        {[
                            { icon: 'message', label: 'WhatsApp Business API' },
                            { icon: 'ai', label: 'OpenAI GPT-4' },
                            { icon: 'chart', label: 'Real-time Analytics' },
                            { icon: 'link', label: 'Webhooks & APIs' },
                            { icon: 'mail', label: 'Email Notifications' },
                            { icon: 'users', label: 'Team Collaboration' },
                            { icon: 'message', label: 'WhatsApp Business API' },
                            { icon: 'ai', label: 'OpenAI GPT-4' },
                            { icon: 'chart', label: 'Real-time Analytics' },
                            { icon: 'link', label: 'Webhooks & APIs' },
                            { icon: 'mail', label: 'Email Notifications' },
                            { icon: 'users', label: 'Team Collaboration' }
                        ].map((item, i) => (
                            <div key={i} className="hiw-integration-pill">
                                <span style={{ color: '#128C7E', display: 'flex' }}><ProfessionalIcon name={item.icon} size={16} /></span>
                                <span>{item.label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="hiw-cta">
                <h2 className="hiw-cta-title">Ready to Get Started?</h2>
                <p className="hiw-cta-sub">Set up your first WhatsApp chatbot in under 30 minutes</p>
                <div className="hiw-cta-btns">
                    <button onClick={() => navigate('/pricing')} className="hiw-btn-white">
                        <span style={{ display: 'inline-flex', verticalAlign: 'middle', marginRight: '6px' }}><ProfessionalIcon name="rocket" size={18} /></span> Start Free Trial
                    </button>
                    <button onClick={() => navigate('/')} className="hiw-btn-outline">
                        ← Back to Home
                    </button>
                </div>
            </div>

            <footer className="ct-footer">
                <div className="ct-container">
                    <div className="ct-footer-grid">
                        <div>
                            <div className="ct-nav-brand" onClick={() => navigate('/')} style={{ gap: '2px' }}>
                                <img src="/logo 1.png" alt="ChatTech24" style={{ height: '44px', width: 'auto', objectFit: 'contain' }} />
                                <span style={{ color: '#D3D3D3', fontFamily: 'Cooper Black', fontSize: '13px', fontWeight: '700' }}>Chat Tech 24</span>
                            </div>
                            <p className="ct-footer-tagline">
                                AI-powered WhatsApp automation platform for modern businesses.
                            </p>
                            <div className="ct-footer-social">
                                <a href="https://vedvika.com" target="_blank" rel="noopener noreferrer" className="ct-footer-social-link">
                                    <ProfessionalIcon name="globe" size={16} /> Website
                                </a>
                                <Link to="/contact" className="ct-footer-social-link">
                                    <ProfessionalIcon name="mail" size={16} /> Contact
                                </Link>
                            </div>
                        </div>
                        <div className="ct-footer-col">
                            <div className="ct-footer-col-title">Product</div>
                            <Link to="/pricing">Pricing</Link>
                            <Link to="/features">Features</Link>
                            <Link to="/services">Services</Link>
                            <Link to="/how-it-works">How It Works</Link>
                        </div>
                        <div className="ct-footer-col">
                            <div className="ct-footer-col-title">Company</div>
                            <a href="https://vedvika.com/#about" target="_blank" rel="noopener noreferrer">About Vedvika</a>
                            <Link to="/contact">Contact Us</Link>
                        </div>
                        <div className="ct-footer-col">
                            <div className="ct-footer-col-title">Support</div>
                            <Link to="/how-it-works">Documentation</Link>
                            <Link to="/contact">Talk to Sales</Link>
                            <a href="mailto:support@chattech24.com">Email Support</a>
                        </div>
                    </div>
                    <div className="ct-footer-bottom">
                        <p className="ct-footer-copy">
                            © 2026 ChatTech24 by{' '}
                            <a href="https://vedvika.com" target="_blank" rel="noopener noreferrer">Vedvika Technologies</a>.
                            All rights reserved.
                        </p>
                        <div className="ct-footer-legal">
                            <a href="https://vedvika.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                            <a href="https://vedvika.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                        </div>
                    </div>
                </div>
            </footer>

            <Login isOpen={loginModalOpen} onClose={() => setLoginModalOpen(false)} />
        </div>
    );
}

export default HowItWorks;